import { isObjectEndpoint } from './graphModel.js';

/** Default spacing of arrangeGraph, in page-local pixels. */
export const GRAPH_ARRANGE_SPACING = Object.freeze({
    // Clearance around the plate and the UI objects.
    plateGap: 40,
    columnGap: 40,
    rowGap: 16,
    // Grid of a node that moves along its row towards its object.
    grid: 10,
});

const overlaps = (a, b) => a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
const inflate = (rect, dx, dy) => ({ x: rect.x - dx, y: rect.y - dy, width: rect.width + 2 * dx, height: rect.height + 2 * dy });
const mean = values => values.reduce((sum, value) => sum + value, 0) / values.length;

/**
 * Longest path in nodes along `edges` ([from, to] ids), growing `dist` from
 * the ids it already holds. Capped at `limit`, so a cycle ends.
 */
function relax(dist, edges, limit) {
    for (let changed = true; changed;) {
        changed = false;
        for (const [from, to] of edges) {
            if (!dist.has(from)) continue;
            const next = dist.get(from) + 1;
            if (next <= limit && next > (dist.get(to) ?? 0)) {
                dist.set(to, next);
                changed = true;
            }
        }
    }
}

/**
 * Side and column of every node. Nodes fed by a UI object (directly or
 * through other nodes) go right of the plate, nodes feeding one go left;
 * the column is the longest path to the object, so every wire runs left to
 * right. Nodes off these flows that feed a right-hand node, or are fed by a
 * left-hand one, join that side; the rest go to a band below the plate.
 * @returns {Map<string, { side: 'left'|'right'|'below', col: number }>}
 */
function assignColumns(nodes, wires) {
    const nodeWires = wires.filter(wire => !isObjectEndpoint(wire.from) && !isObjectEndpoint(wire.to));
    const right = new Map();
    const left = new Map();
    for (const wire of wires) {
        if (isObjectEndpoint(wire.from) && !isObjectEndpoint(wire.to)) right.set(wire.to.node, 1);
        if (isObjectEndpoint(wire.to) && !isObjectEndpoint(wire.from)) left.set(wire.from.node, 1);
    }
    relax(right, nodeWires.map(wire => [wire.from.node, wire.to.node]), nodes.length);
    relax(left, nodeWires.map(wire => [wire.to.node, wire.from.node]), nodes.length);

    const columns = new Map();
    for (const node of nodes) {
        const r = right.get(node.id);
        const l = left.get(node.id);
        if (r !== undefined && (l === undefined || r <= l)) columns.set(node.id, { side: 'right', col: r });
        else if (l !== undefined) columns.set(node.id, { side: 'left', col: l });
    }
    for (let changed = true; changed;) {
        changed = false;
        for (const wire of nodeWires) {
            const from = columns.get(wire.from.node);
            const to = columns.get(wire.to.node);
            if (!from && to?.side === 'right') columns.set(wire.from.node, { side: 'right', col: Math.max(1, to.col - 1) });
            else if (from?.side === 'left' && !to) columns.set(wire.to.node, { side: 'left', col: Math.max(1, from.col - 1) });
            else continue;
            changed = true;
        }
    }

    const below = new Map(nodes.filter(node => !columns.has(node.id)).map(node => [node.id, 0]));
    relax(below, nodeWires.filter(wire => below.has(wire.from.node) && below.has(wire.to.node))
        .map(wire => [wire.from.node, wire.to.node]), nodes.length);
    for (const [id, col] of below) columns.set(id, { side: 'below', col });
    return columns;
}

/**
 * Tops for a column of boxes in their given order, at least `rowGap` apart
 * and as close to their targets as possible (weighted least squares, by
 * pooling adjacent violators), none above `minY`.
 */
function packColumn(items, rowGap, minY) {
    const offsets = [];
    let offset = 0;
    for (const item of items) {
        offsets.push(offset);
        offset += item.height + rowGap;
    }
    const blocks = [];
    items.forEach((item, index) => {
        blocks.push({ sum: (item.target - offsets[index]) * item.weight, weight: item.weight, count: 1 });
        while (blocks.length > 1) {
            const last = blocks[blocks.length - 1];
            const prev = blocks[blocks.length - 2];
            if (prev.sum / prev.weight <= last.sum / last.weight) break;
            prev.sum += last.sum;
            prev.weight += last.weight;
            prev.count += last.count;
            blocks.pop();
        }
    });
    const tops = [];
    for (const block of blocks) {
        const base = Math.max(minY, block.sum / block.weight);
        for (let k = 0; k < block.count; k++) tops.push(base + offsets[tops.length]);
    }
    return tops;
}

/**
 * Arrange a page graph around the plate: neat columns and rows, short
 * wires to the UI objects and no crossing wires.
 *
 * 1. Columns (see assignColumns): nodes fed by objects right of the plate,
 *    nodes feeding objects left of it, each flow one column further out per
 *    step; nodes that touch no object in a band below the plate.
 * 2. Rows of the column next to the plate, one per object wire, each on the
 *    side of the plate where that wire is shortest: beside it, level with
 *    the object; or, for an object near the top or bottom edge, stacked
 *    above or below the plate. Objects side by side share a height: the one
 *    nearest the column gets the straight wire and the others fan out above
 *    it, those further away further out, so their wires pass around one
 *    another instead of across.
 * 3. Nodes further out line up with the nodes they hang from, so wires
 *    between nodes run straight; every column is then spread out without
 *    overlaps, in that order.
 * 4. A row clear of the plate (above or below it) moves its first node along
 *    the row, up to its object, and the rest of the row follows to the
 *    nearest free columns.
 * Pure and deterministic.
 *
 * @param {object} graph
 * @param {object} layout
 * @param {Array}  layout.objects   [{ uuid, rect }] the page's UI objects, page-local
 * @param {{width,height}} layout.plate
 * @param {(node) => { width, height, port: (dir: 'in'|'out', port) => {x,y} }} layout.nodeShape
 *        box of a node, and where a port sits relative to its top left
 * @param {(uuid, port, dir: 'in'|'out') => {x,y}} layout.objectPort where a wire attaches to an object
 * @param {object} [layout.spacing] overrides of GRAPH_ARRANGE_SPACING
 * @returns {object} the graph with its nodes moved
 */
export function arrangeGraph(graph, { objects = [], plate = { width: 0, height: 0 }, nodeShape, objectPort, spacing = {} }) {
    const nodes = graph?.nodes ?? [];
    if (nodes.length === 0) return graph;
    const gap = { ...GRAPH_ARRANGE_SPACING, ...spacing };
    const shapes = new Map(nodes.map(node => [node.id, nodeShape(node)]));
    const objectIds = new Set(objects.map(object => object.uuid));
    const known = end => (isObjectEndpoint(end) ? objectIds.has(end.object) : shapes.has(end.node));
    const wires = graph.wires.filter(wire => known(wire.from) && known(wire.to));
    const columns = assignColumns(nodes, wires);
    const portY = (id, dir, port) => shapes.get(id).port(dir, port).y;

    const neighbours = new Map(nodes.map(node => [node.id, []]));
    for (const wire of wires) {
        if (isObjectEndpoint(wire.from) || isObjectEndpoint(wire.to)) continue;
        neighbours.get(wire.from.node).push(wire.to.node);
        neighbours.get(wire.to.node).push(wire.from.node);
    }
    const nearerThan = column => id => {
        const other = columns.get(id);
        return other.side === column.side && other.col < column.col;
    };

    const obstacles = [{ x: 0, y: 0, ...plate }, ...objects.map(object => object.rect)]
        .map(rect => inflate(rect, gap.plateGap, gap.plateGap));
    // On the grid, so the columns line up with nodes moved along their rows.
    const area = {
        left: Math.floor(Math.min(...obstacles.map(rect => rect.x)) / gap.grid) * gap.grid,
        top: Math.floor(Math.min(...obstacles.map(rect => rect.y)) / gap.grid) * gap.grid,
        right: Math.ceil(Math.max(...obstacles.map(rect => rect.x + rect.width)) / gap.grid) * gap.grid,
        bottom: Math.ceil(Math.max(...obstacles.map(rect => rect.y + rect.height)) / gap.grid) * gap.grid,
    };
    const maxWidth = Math.max(...[...shapes.values()].map(shape => shape.width));
    const pitch = maxWidth + gap.columnGap;
    const columnX = (id, side, col) => {
        if (side === 'right') return area.right + (col - 1) * pitch;
        if (side === 'left') return area.left - (col - 1) * pitch - shapes.get(id).width;
        return area.left + gap.plateGap + col * pitch;
    };

    // Where the objects wired to a node next to the plate attach (their mean), or null.
    const objectPoint = (id, side) => {
        const points = [];
        for (const wire of wires) {
            if (side === 'right' && wire.to.node === id && isObjectEndpoint(wire.from)) points.push(objectPort(wire.from.object, wire.from.port, 'out'));
            if (side === 'left' && wire.from.node === id && isObjectEndpoint(wire.to)) points.push(objectPort(wire.to.object, wire.to.port, 'in'));
        }
        return points.length ? { x: mean(points.map(point => point.x)), y: mean(points.map(point => point.y)) } : null;
    };

    const tops = new Map();
    // Where each wire end of node `id` would put its top: level with the other
    // end, an object or a placed node that `accept`s.
    const wishes = (id, accept) => {
        const result = [];
        for (const wire of wires) {
            for (const [own, other, dir, otherDir] of [[wire.to, wire.from, 'in', 'out'], [wire.from, wire.to, 'out', 'in']]) {
                if (own.node !== id) continue;
                let y;
                if (isObjectEndpoint(other)) y = objectPort(other.object, other.port, otherDir).y;
                else if (tops.has(other.node) && accept(other.node)) y = tops.get(other.node) + portY(other.node, otherDir, other.port);
                else continue;
                result.push(y - portY(id, dir, own.port));
            }
        }
        return result;
    };

    // Rows of the column next to the plate (step 2), per node: band, sort key,
    // tie-break, target and weight (see packColumn).
    const lanes = new Map();
    const bands = { above: { right: [], left: [] }, below: { right: [], left: [] } };
    for (const side of ['right', 'left']) {
        for (const node of nodes) {
            const column = columns.get(node.id);
            const point = column.side === side && column.col === 1 ? objectPoint(node.id, side) : null;
            if (!point) continue;
            const { height } = shapes.get(node.id);
            const beside = side === 'right' ? area.right - point.x : point.x - area.left;
            const above = point.y - area.top + height / 2 + gap.plateGap;
            const below = area.bottom - point.y + height / 2 + gap.plateGap;
            const band = above < beside && above <= below ? 'above' : below < beside ? 'below' : 'beside';
            const straight = mean(wishes(node.id, () => false));
            if (band !== 'beside') {
                bands[band][side].push({ id: node.id, point, height });
                continue;
            }
            // Objects side by side start with the one furthest from the column on
            // top; the weight keeps the nearest one's wire straight, as a vertical
            // offset costs a long wire less length than a short one.
            lanes.set(node.id, {
                band, point, straight, target: straight, key: point.y,
                tie: side === 'right' ? point.x : -point.x, weight: 1 / Math.max(gap.columnGap, beside),
            });
        }
    }
    // Rows above and below the plate, shared by both sides and stacked outwards:
    // nearest the plate the lanes nearest their columns, so the wires of the
    // others pass beside them. A left and a right lane share a row when their
    // nodes stay apart; otherwise the one the other lanes would cross less goes first.
    for (const [band, dir] of [['above', -1], ['below', 1]]) {
        const right = bands[band].right.sort((a, b) => b.point.x - a.point.x);
        const left = bands[band].left.sort((a, b) => a.point.x - b.point.x);
        let edge = band === 'above' ? area.top : area.bottom;
        while (right.length > 0 || left.length > 0) {
            let row;
            if (right.length > 0 && left.length > 0 && left[0].point.x < right[0].point.x) row = [right.shift(), left.shift()];
            else if (right.length > 0 && left.length > 0) {
                const rightFirst = left.filter(lane => lane.point.x > right[0].point.x).length;
                const leftFirst = right.filter(lane => lane.point.x < left[0].point.x).length;
                row = [rightFirst <= leftFirst ? right.shift() : left.shift()];
            } else row = [(right.length > 0 ? right : left).shift()];
            for (const lane of row) {
                const target = dir < 0 ? edge - lane.height : edge;
                lanes.set(lane.id, { band, point: lane.point, target, key: target + lane.height / 2, tie: 0, weight: 1 });
            }
            edge += dir * (Math.max(...row.map(lane => lane.height)) + gap.rowGap);
        }
    }

    // Trade rows between lanes beside the plate while that shortens their
    // wires to the objects in total: then no two of those wires cross.
    const tradeRows = (items, rows, side) => {
        const x = side === 'right' ? area.right : area.left;
        const cost = (item, top) => Math.hypot(x - item.point.x, top - item.straight);
        let traded = false;
        for (let i = 0; i < items.length; i++) {
            for (let j = i + 1; j < items.length; j++) {
                const a = items[i];
                const b = items[j];
                if (a.band !== 'beside' || b.band !== 'beside') continue;
                if (cost(a, rows[j]) + cost(b, rows[i]) < cost(a, rows[i]) + cost(b, rows[j]) - 0.5) {
                    items[i] = b;
                    items[j] = a;
                    traded = true;
                }
            }
        }
        return traded;
    };

    const groups = new Map();
    for (const node of nodes) {
        const { side, col } = columns.get(node.id);
        const key = `${side}:${col}`;
        if (!groups.has(key)) groups.set(key, { side, col, nodes: [] });
        groups.get(key).nodes.push(node);
    }
    const sideOrder = { right: 0, left: 1, below: 2 };
    const ordered = [...groups.values()].sort((a, b) => sideOrder[a.side] - sideOrder[b.side] || a.col - b.col);
    const sideGroups = ordered.filter(group => group.side !== 'below');
    const belowGroups = ordered.filter(group => group.side === 'below');

    // Step 3: line up with the nodes nearer the plate (else with any placed
    // neighbour, else keep the height), then spread the column out.
    const place = (group, minY) => {
        const nearer = nearerThan(group);
        const items = group.nodes.map((node, index) => {
            const height = shapes.get(node.id).height;
            const lane = lanes.get(node.id);
            if (lane) return { node, index, height, ...lane };
            const anchored = wishes(node.id, nearer);
            const any = anchored.length ? anchored : wishes(node.id, () => true);
            const target = any.length ? mean(any) : group.side === 'below' ? minY : tops.get(node.id) ?? node.y;
            return { node, index, height, target, key: target + height / 2, tie: 0, weight: 1 / pitch };
        });
        items.sort((a, b) => a.key - b.key || a.tie - b.tie || a.index - b.index);
        let rows = packColumn(items, gap.rowGap, minY);
        for (let round = 0; round < 8 && tradeRows(items, rows, group.side); round++) rows = packColumn(items, gap.rowGap, minY);
        rows.forEach((top, i) => tops.set(items[i].node.id, top));
        // Keep this order for the next pass, so ties stay put.
        group.nodes = items.map(item => item.node);
    };
    // The second pass lines up nodes whose neighbours were not placed yet in the first.
    for (let pass = 0; pass < 2; pass++) sideGroups.forEach(group => place(group, -Infinity));

    const rects = new Map();
    const rectAt = (id, x) => ({ x, y: tops.get(id), width: shapes.get(id).width, height: shapes.get(id).height });
    for (const group of sideGroups) {
        for (const node of group.nodes) rects.set(node.id, rectAt(node.id, columnX(node.id, group.side, group.col)));
    }
    const isFree = (rect, self) => {
        if (obstacles.some(other => overlaps(rect, other))) return false;
        for (const [id, other] of rects) {
            if (id !== self && overlaps(rect, inflate(other, gap.columnGap, gap.rowGap))) return false;
        }
        return true;
    };
    // Move node `id` to the first free spot from `x` towards `limit`, one grid step at a time.
    const slide = (id, x, limit) => {
        const rect = rects.get(id);
        const step = x < limit ? gap.grid : -gap.grid;
        for (; step > 0 ? x < limit : x > limit; x += step) {
            const moved = { ...rect, x };
            if (isFree(moved, id)) {
                rects.set(id, moved);
                return;
            }
        }
    };

    // Step 4: first nodes of rows clear of the plate, then the rest of their rows.
    for (const group of sideGroups.filter(item => item.col === 1)) {
        for (const node of group.nodes) {
            const point = objectPoint(node.id, group.side);
            const { x, y, width, height } = rects.get(node.id);
            if (!point || (y + height > area.top && y < area.bottom)) continue;
            if (group.side === 'right') slide(node.id, Math.ceil((point.x + gap.plateGap) / gap.grid) * gap.grid, x);
            else slide(node.id, Math.floor((point.x - gap.plateGap - width) / gap.grid) * gap.grid, x);
        }
    }
    for (const group of sideGroups.filter(item => item.col > 1)) {
        const right = group.side === 'right';
        for (const node of group.nodes) {
            const anchors = neighbours.get(node.id).filter(nearerThan(group)).map(id => rects.get(id));
            if (anchors.length === 0) continue;
            const rect = rects.get(node.id);
            const limit = right
                ? Math.max(...anchors.map(anchor => anchor.x + anchor.width)) + gap.columnGap
                : Math.min(...anchors.map(anchor => anchor.x)) - gap.columnGap - rect.width;
            for (let col = 1; col < group.col; col++) {
                const x = columnX(node.id, group.side, col);
                if (right ? x < limit : x > limit) continue;
                const moved = { ...rect, x };
                if (isFree(moved, node.id)) {
                    rects.set(node.id, moved);
                    break;
                }
            }
        }
    }

    // The band below the plate starts under whatever it would hit.
    if (belowGroups.length > 0) {
        const from = columnX(null, 'below', 0);
        const to = columnX(null, 'below', Math.max(...belowGroups.map(group => group.col))) + maxWidth;
        let minY = area.bottom;
        for (const rect of rects.values()) {
            if (rect.x < to && rect.x + rect.width > from) minY = Math.max(minY, rect.y + rect.height + gap.plateGap);
        }
        for (let pass = 0; pass < 2; pass++) belowGroups.forEach(group => place(group, minY));
        for (const group of belowGroups) {
            for (const node of group.nodes) rects.set(node.id, rectAt(node.id, columnX(node.id, 'below', group.col)));
        }
    }

    return {
        ...graph,
        nodes: nodes.map(node => {
            const x = Math.round(rects.get(node.id).x);
            const y = Math.round(rects.get(node.id).y);
            return x === node.x && y === node.y ? node : { ...node, x, y };
        }),
    };
}
